'use client';

import { XMarkIcon } from '@heroicons/react/20/solid';
import { BuildingOffice2Icon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useIsTattooPage } from '@/hooks/useIsTattooPage';
import { track } from '@/lib/analytics';
import { useGetContent } from '@/lib/react-query/ContentQueries';
import { sendContactEmail } from '@/lib/server/contact.server';
import { cn } from '@/lib/utils';

import Alert from '../Alert';
import Button from '../Button/Button';
import Input from '../Form/Input';
import Label from '../Form/Label';
import Textarea from '../Form/Textarea';

type ContactValues = {
  first_name: string;
  last_name: string;
  email: string;
  message: string;
};

type Props = {
  className?: string;
};

const Contact = ({ className = '' }: Props) => {
  const { data: content, error, isLoading } = useGetContent();
  const isTattooPage = useIsTattooPage();
  const { register, reset, handleSubmit } = useForm<ContactValues>();
  const [submitClicked, setSubmitClicked] = useState(false);
  const [contactResult, setContactResult] = useState<{
    message: string;
    type: 'success' | 'danger';
  } | null>(null);

  useEffect(() => {
    return () => setContactResult(null);
  }, []);

  const handleSendMessage = handleSubmit(async (data) => {
    setContactResult(null);
    setSubmitClicked(true);

    try {
      const result = await sendContactEmail({ data });

      setSubmitClicked(false);

      if (!result.error) {
        reset();
        track('ContactFormSubmit', { page: isTattooPage ? 'tattoo' : 'piercing' });
        setContactResult({
          message: 'Votre message a bien été envoyé',
          type: 'success',
        });
      } else {
        setContactResult({
          message:
            "Une erreur est survenue lors de l'envoi de votre message. Vous pouvez aussi me contacter par téléphone ou via Instagram.",
          type: 'danger',
        });
      }
    } catch {
      setSubmitClicked(false);
      setContactResult({
        message:
          "Une erreur est survenue lors de l'envoi de votre message. Vous pouvez aussi me contacter par téléphone ou via Instagram.",
        type: 'danger',
      });
    }
  });

  const contact = content?.contact;

  if (isLoading || !content || !contact || error) {
    return null;
  }

  const borderColor = isTattooPage ? 'border-y-emerald-100' : 'border-y-amber-100';
  const bgColor = isTattooPage ? 'bg-emerald-100/30' : 'bg-amber-100/30';

  return (
    <div
      className={cn(
        'relative isolate mb-8 w-full skew-y-6 transform border-y py-24 lg:skew-y-3',
        borderColor,
        bgColor,
        className,
      )}
    >
      <div className="mx-auto grid max-w-7xl -skew-y-6 transform grid-cols-1 lg:-skew-y-3 lg:grid-cols-2">
        <div className="relative px-6 py-4 lg:static lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">Entrer en contact</h2>
            {contact.description ? (
              <p className="mt-6 text-lg leading-8 text-gray-600">{contact.description}</p>
            ) : null}

            <dl className="mt-10 space-y-4 text-base leading-7 text-gray-600">
              {contact.address ? (
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Adresse</span>
                    <BuildingOffice2Icon className="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>{contact.address}</dd>
                </div>
              ) : null}

              {contact.phone ? (
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Téléphone</span>
                    <PhoneIcon className="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>
                    <a className="hover:text-gray-900" href={`tel:${contact.phone}`}>
                      {contact.phone}
                    </a>
                  </dd>
                </div>
              ) : null}

              {contact.email ? (
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">E-mail</span>
                    <EnvelopeIcon className="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>
                    <a className="hover:text-gray-900" href="mailto:azelysse.piercing@gmail.com">
                      {contact.email}
                    </a>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>
        <form onSubmit={handleSendMessage} className="px-6 lg:px-8">
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            {contactResult ? (
              <div className="mb-6">
                <Alert
                  variant={contactResult.type}
                  actions={
                    <Button size="md" color="soft" onClick={() => setContactResult(null)}>
                      <XMarkIcon
                        className={cn(
                          'h-5 w-5',
                          contactResult.type === 'success' ? 'text-emerald-900' : 'text-rose-900',
                        )}
                      />
                    </Button>
                  }
                >
                  {contactResult.message}
                </Alert>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="first-name">Prénom</Label>
                <div className="mt-2.5">
                  <Input
                    type="text"
                    id="first-name"
                    autoComplete="given-name"
                    required
                    {...register('first_name')}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="last-name">Nom</Label>
                <div className="mt-2.5">
                  <Input
                    type="text"
                    id="last-name"
                    autoComplete="family-name"
                    required
                    {...register('last_name')}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="mt-2.5">
                  <Input
                    type="email"
                    id="email"
                    required
                    autoComplete="email"
                    {...register('email')}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message">Message</Label>
                <div className="mt-2.5">
                  <Textarea
                    id="message"
                    rows={4}
                    defaultValue={''}
                    required
                    {...register('message')}
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <Button type="submit" size="lg" isLoading={submitClicked} disabled={submitClicked}>
                Envoyer
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Contact;
